import { useCallback } from 'react';
import { FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import { useUpdateUserCustomFields, useDeleteUserCustomField } from '../../query/resource-hooks/customFields';
import { useSuToast } from '../useSuToast';
import { CustomFieldDefinition, UpdateUserCustomFieldsRequest, CustomFieldFormValues } from '../../types/customFields';

export interface UseCustomFieldsActionsReturn {
    // Main actions
    updateFields: (
        apiRequest: UpdateUserCustomFieldsRequest,
        fieldsToDelete: CustomFieldDefinition[],
        formikHelpers?: FormikHelpers<CustomFieldFormValues>
    ) => Promise<void>;

    deleteField: (fieldName: string) => Promise<void>;

    // State
    isUpdating: boolean;
    isDeleting: boolean;
    hasError: boolean;
    error: any;

    // Utility
    resetError: () => void;
}

/**
 * Hook for managing custom fields API operations
 * Handles update, delete operations with error handling and user feedback
 */
export const useCustomFieldsActions = (
    userId: number,
    definitions: CustomFieldDefinition[],
    onSuccess?: () => void
): UseCustomFieldsActionsReturn => {
    const intl = useIntl();
    const toast = useSuToast();

    // Update mutation
    const updateMutation = useUpdateUserCustomFields(
        (_data, _variables) => {
            toast.success(intl.formatMessage({ id: 'customFields.success' }));
            onSuccess?.();
        },
        (error) => {
            // Don't show toast error here - let the component handle validation errors
            console.error('Custom fields update error:', error);
        }
    );

    // Delete mutation
    const deleteMutation = useDeleteUserCustomField(
        () => {
            // Success handled silently for individual field deletions
        },
        (error) => {
            toast.error(intl.formatMessage({ id: 'customFields.deleteError' }));
            console.error('Custom field delete error:', error);
        }
    );

    // Main update function with field deletion and error handling
    const updateFields = useCallback(
        async (
            apiRequest: UpdateUserCustomFieldsRequest,
            fieldsToDelete: CustomFieldDefinition[],
            formikHelpers?: FormikHelpers<CustomFieldFormValues>
        ) => {
            try {
                // First, delete removed fields
                for (const definition of fieldsToDelete) {
                    await deleteMutation.mutateAsync({
                        userId,
                        fieldName: definition.name,
                    });
                }

                // Then update/create selected fields
                if (apiRequest.values.length === 0) {
                    // No fields to update, just show success
                    toast.success(intl.formatMessage({ id: 'customFields.success' }));
                    onSuccess?.();
                    return;
                }

                await updateMutation.mutateAsync({
                    userId,
                    data: apiRequest,
                });
            } catch (error: any) {
                // Handle validation errors from backend
                if (error?.response?.status === 422 && error?.response?.data?.errors && formikHelpers) {
                    const backendErrors = error.response.data.errors;
                    const formikErrors: Record<string, string> = {};

                    // Transform backend errors to Formik format
                    backendErrors.forEach((err: { field: string; message: string }) => {
                        // Find the definition ID for this field name
                        const definition = definitions.find((d) => d.name === err.field);
                        if (definition) {
                            formikErrors[definition.id.toString()] = err.message;
                        }
                    });

                    // Set errors in Formik
                    formikHelpers.setErrors(formikErrors);
                } else {
                    // Show general error for non-validation errors
                    toast.error(intl.formatMessage({ id: 'customFields.error' }));
                }

                throw error; // Re-throw to let calling component handle if needed
            }
        },
        [userId, definitions, updateMutation, deleteMutation, toast, intl, onSuccess]
    );

    // Individual field deletion
    const deleteField = useCallback(
        async (fieldName: string) => {
            await deleteMutation.mutateAsync({ userId, fieldName });
        },
        [userId, deleteMutation]
    );

    // Reset error state
    const resetError = useCallback(() => {
        // Reset any error states if needed
    }, []);

    return {
        updateFields,
        deleteField,
        isUpdating: updateMutation.isLoading,
        isDeleting: deleteMutation.isLoading,
        hasError: updateMutation.isError || deleteMutation.isError,
        error: updateMutation.error || deleteMutation.error,
        resetError,
    };
};

export default useCustomFieldsActions;
